import { AfterViewChecked, Component, ElementRef, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as fromApp from '../../../store/app.reducers';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { pageAnimation } from '@app/core/animations/element.animations';
import { AuthConstants } from '@app/constants/application-constants';
import { AuthClient, ResendVerificationEmailRequest, VerifyEmailRequest } from '../../../clients/client.generated';

@Component({
    selector: 'app-verify',
    templateUrl: './verify.component.html',
    styleUrls: ['./verify.component.scss'],
    animations: [pageAnimation]
})
export class VerifyComponent implements AfterViewChecked, OnInit, OnDestroy {

    @HostBinding('@pageAnimation') animate = true;
    email = '';
    isVerifying = false;
    isFromCreateUser = false;
    continueUri: string;
    isVerifyingEmail = false;
    isReverification = false;
    state: 'prompt' | 'sending' | 'sent' | 'verifying' | 'failed' = 'prompt';
    failureMessage = '';
    tokenVerificationFailed = false;

    private readonly unsubscribe$ = new Subject<void>();
    private resendRequest?: Subscription;
    private verificationRequest?: Subscription;
    private focusReverificationHeading = false;
    private routeContextKey?: string;

    @ViewChild('reverificationTitle') private reverificationTitle?: ElementRef<HTMLHeadingElement>;

    constructor(
        private route: ActivatedRoute,
        private store: Store<fromApp.AppState>,
        private router: Router,
        private notification: NzNotificationService,
        private authClient: AuthClient
    ) { }

    ngOnInit(): void {
        if (localStorage.getItem(AuthConstants.PROFILE_LOCAL_STORAGE_KEY)) {
            this.router.navigate(['/']);
            return;
        }

        const loginEntry = localStorage.getItem('login-entry');
        if (loginEntry) {
            this.isFromCreateUser = loginEntry === 'create-user';
            localStorage.removeItem('login-entry');
        }

        this.route.queryParamMap
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(params => this.handleRouteParameters(params));
    }

    ngOnDestroy(): void {
        this.cancelPendingRequests();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngAfterViewChecked(): void {
        if (this.focusReverificationHeading && this.reverificationTitle) {
            this.reverificationTitle.nativeElement.focus();
            this.focusReverificationHeading = false;
        }
    }

    onReSendEmail(): void {
        if (!this.email || this.resendRequest || this.state === 'sending' || this.state === 'verifying') {
            return;
        }

        const request = new ResendVerificationEmailRequest({ email: this.email });
        const isReverification = this.isReverification;
        this.failureMessage = '';
        this.state = 'sending';
        const requestSubscription = new Subscription();
        this.resendRequest = requestSubscription;
        requestSubscription.add(this.authClient.resendVerificationEmail(request)
            .pipe(
                take(1),
                takeUntil(this.unsubscribe$),
                finalize(() => {
                    if (this.resendRequest === requestSubscription) {
                        this.resendRequest = undefined;
                    }
                    if (!isReverification && this.state === 'sending') {
                        this.state = 'prompt';
                    }
                })
            )
            .subscribe({
                next: () => {
                    if (isReverification) {
                        this.state = 'sent';
                    } else {
                        this.notification.success('Confirmation Email', 'Confirmation e-mail sent');
                        this.state = 'prompt';
                    }
                },
                error: () => {
                    if (isReverification) {
                        this.failureMessage = 'We could not send the verification email. Please try again.';
                        this.state = 'failed';
                    } else {
                        this.notification.error(
                            'Confirmation Email',
                            'Something went wrong. Confirmation e-mail not sent'
                        );
                    }
                }
            }));
    }

    goToLogin(): void {
        this.router.navigate(['/account/login'], {
            queryParams: this.email ? { email: this.email } : {}
        });
    }

    verifyEmailWithToken(token: string): void {
        this.tokenVerificationFailed = false;
        this.failureMessage = '';
        this.isVerifyingEmail = true;
        this.state = 'verifying';

        const request = new VerifyEmailRequest({ token });
        const requestSubscription = this.authClient.verifyEmail(request)
            .pipe(take(1), takeUntil(this.unsubscribe$))
            .subscribe({
                next: response => {
                    this.isVerifyingEmail = false;
                    this.notification.success(
                        'Email Verified Successfully',
                        'Thank you for verifying your email address. You can now log in to your account.', {
                            nzDuration: 5000
                        }
                    );

                    this.router.navigate(['/account/login'], {
                        queryParams: response.email ? { email: response.email } : {}
                    });
                },
                error: error => {
                    this.isVerifyingEmail = false;
                    this.tokenVerificationFailed = true;
                    this.failureMessage = this.getVerificationFailureMessage(error);
                    this.state = 'failed';
                    this.handleVerificationError(this.failureMessage);
                    if (!this.email && [400, 404, 410].includes(error?.status)) {
                        this.router.navigate(['/account/login'], { queryParams: {} });
                    }
                }
            });

        if (!requestSubscription.closed) {
            this.verificationRequest = requestSubscription;
        }
    }

    private handleRouteParameters(params: ParamMap): void {
        const token = params.get('token')?.trim() ?? '';
        const routeEmail = params.get('email')?.trim() ?? '';
        const reason = params.get('reason');
        const isReverification = reason === 'reverification';
        const routeContextKey = JSON.stringify([
            routeEmail.normalize('NFC').toLowerCase(),
            token,
            isReverification
        ]);

        if (this.routeContextKey === routeContextKey) {
            return;
        }
        this.routeContextKey = routeContextKey;

        // Capture all route context before starting token work so failures retain a supplied email.
        this.cancelPendingRequests();
        this.email = routeEmail;
        const wasReverification = this.isReverification;
        this.isReverification = isReverification;
        if (this.isReverification && !wasReverification) {
            this.focusReverificationHeading = true;
        } else if (!this.isReverification) {
            this.focusReverificationHeading = false;
        }
        this.tokenVerificationFailed = false;
        this.isVerifyingEmail = false;
        this.failureMessage = '';
        this.state = 'prompt';

        if (token) {
            this.verifyEmailWithToken(token);
            return;
        }

        if (!routeEmail) {
            this.router.navigate(['/account/login'], { queryParams: {} });
            return;
        }

        if (this.isReverification) {
            this.onReSendEmail();
        }
    }

    private getVerificationFailureMessage(error: any): string {
        if (error?.status === 400) {
            return 'Invalid verification token. Please check your email for the correct verification link.';
        }
        if (error?.status === 404) {
            return 'Invalid or expired verification token. Please request a new verification email.';
        }
        if (error?.status === 410) {
            return 'Verification token has expired. Please request a new verification email.';
        }
        return error?.error?.message ?? 'An error occurred during email verification. Please try again.';
    }

    private handleVerificationError(errorMessage: string): void {
        this.notification.error('Email Verification Failed', errorMessage);
    }

    private cancelPendingRequests(): void {
        this.resendRequest?.unsubscribe();
        this.resendRequest = undefined;
        this.verificationRequest?.unsubscribe();
        this.verificationRequest = undefined;
    }
}
